import {
  type Component,
  Show,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

import { warmUp } from "./search/embed";
import { getEpisodes } from "./search/episodes";
import { searchHybrid } from "./search/hybrid";
import type { Episode, Hit } from "./search/types";
import { CardStack, type StackItem } from "./ui/CardStack";
import { Loading } from "./ui/Loading";
import { QuoteRotator } from "./ui/QuoteRotator";
import { SearchBox } from "./ui/SearchBox";

const DEBOUNCE_MS = 600;
const STACK_SIZE = 3;

function useDebounced<T>(source: () => T, delay: number): () => T {
  const [debounced, setDebounced] = createSignal(source());
  createEffect(
    on(source, (value) => {
      const t = setTimeout(() => setDebounced(() => value), delay);
      onCleanup(() => clearTimeout(t));
    }),
  );
  return debounced;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function metaLine(ep: Episode | undefined): string {
  if (!ep) return "";
  return [formatDate(ep.pub_date), formatDuration(ep.duration_seconds)].filter(Boolean).join(" · ");
}

function bodyFor(ep: Episode | undefined): string {
  if (!ep) return "";
  return ep.tldr ?? ep.description?.replace(/<[^>]*>/g, "").trim() ?? "";
}

function hitAsStackItem(hit: Hit, episode: Episode | undefined): StackItem {
  return {
    key: `${hit.episode}#${hit.id}`,
    eyebrow: hit.section ?? hit.kind,
    title: episode?.title ?? hit.episode,
    meta: metaLine(episode),
    body: bodyFor(episode) || hit.text,
    spotifyUrl: episode?.spotify_url || undefined,
    applePodcastsUrl: episode?.apple_url || undefined,
    // Always include the match info; UI renders it as a popup-on-demand.
    match: { section: hit.section ?? hit.kind, excerpt: hit.text },
  };
}

export const App: Component = () => {
  const [query, setQuery] = createSignal("");
  const debouncedQuery = useDebounced(query, DEBOUNCE_MS);

  const [episodes] = createResource<Map<string, Episode>>(() => getEpisodes());

  // Kick off model + DB downloads in parallel with the page paint.
  void warmUp();

  const [hits] = createResource<Hit[], string>(
    () => debouncedQuery().trim(),
    async (q) => (q ? searchHybrid(q, STACK_SIZE) : []),
  );

  const [hasEverSucceeded, setHasEverSucceeded] = createSignal(false);
  createEffect(() => {
    if (!hits.loading && hits() !== undefined) setHasEverSucceeded(true);
  });

  const trimmedQuery = createMemo(() => debouncedQuery().trim());
  const showResults = () => trimmedQuery().length > 0;
  const isSearching = () => hits.loading && trimmedQuery().length > 0;

  const stackItems = createMemo<StackItem[]>(() => {
    if (!showResults()) return [];
    const list = hits() ?? [];
    const map = episodes();
    return list.map((h) => hitAsStackItem(h, map?.get(h.episode)));
  });

  return (
    <>
      <main classList={{ "with-results": showResults() }}>
        <header class="brand-lockup">
          <p class="pre-title">The Coffee Podcast</p>
          <h1>People-Focused Coffee Talk</h1>
          <p class="tagline">Search 196 episodes — from before the seed to after the cup.</p>
        </header>

        <section class="search-section">
          <SearchBox value={query()} onInput={setQuery} />

          <Show when={isSearching()}>
            <Loading firstRun={!hasEverSucceeded()} />
          </Show>

          <Show when={!isSearching() && showResults() && (hits() ?? []).length === 0}>
            <p class="empty-state">
              No matches for <em>{trimmedQuery()}</em>. Try a different phrasing or topic.
            </p>
          </Show>

          <Show when={!isSearching() && showResults() && stackItems().length > 0}>
            <div class="stack-eyebrow">Best matches</div>
            <CardStack items={stackItems()} moreLabel="More episode matches" />
          </Show>

          <Show when={!showResults()}>
            <QuoteRotator />
          </Show>
        </section>
      </main>

      <footer class="site-footer">
        <a href="https://thecoffeepodcast.com">thecoffeepodcast.com</a>
      </footer>
    </>
  );
};
